import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { FaSpinner, FaTimes } from "react-icons/fa";
import { uploadSignedCopy, getProjectFileLink, getProjectThumbUrl, createWerkbon } from "./services/projectDetailService";
import SignPdfDialog from "./SignPdfDialog";

// 22/10/2026 (Bartel): een pdf (bv. het RAMI-mandaat) moet meteen opengaan - op de pc in de browser, op
// de gsm in Acrobat of de standaard pdf-app - en niet als naamloos bestand gedownload worden. Daarnaast
// kan de werker het op de gsm ondertekende exemplaar terugzetten: het komt als "<naam>-ondertekend.pdf"
// in de projectmap zelf (sinds 26/10/2026 niet meer in voorafDocs, dus ook niet meer in deze lijst).
// 24/10/2026 (Bartel): tekenen in de pdf-viewer van de browser werkt niet overal (Chrome op de pc
// liet het niet toe). Daarom de knop "Ondertekenen": tekenen gebeurt in de app zelf (SignPdfDialog),
// in elke browser. De knop "Ondertekend exemplaar toevoegen" blijft als uitweg voor wie toch in
// Acrobat tekende.
// 27/10/2026 (Bartel, "voorafDocs sneller"): de lijst komt zonder inhoud van de backend. Een pdf of
// ander document opent via een tijdelijke Dropbox-link in een nieuw tabblad; foto's tonen eerst als
// thumbnail (via de axios-instantie met JWT, als blob-url) en openen groot in een viewer bij een tik.
const isImage = (name) => /\.(jpg|jpeg|png|gif|webp|heic|bmp|tif|tiff)$/i.test(name || "");
const isPdf = (name) => /\.pdf$/i.test(name || "");
const isSigned = (name) => /-ondertekend(\s*\(\d+\))?\.pdf$/i.test(name || "");
// 27/10/2026 (Bartel, workflow "dringende interventie"): bij "Tekenen" van een mandaat (naam bevat
// mandat, mandaat of assigner) maakt de backend eerst het ingevulde exemplaar (uren, materialen,
// beschrijving en foto's als bijlage achter het mandaat) en opent het tekenvenster op dat bestand.
// Een bestand dat al op " - ingevuld.pdf" eindigt, opent meteen. Bovenaan staat de knop "Werkbon maken
// en tekenen" met de taalkeuze NL/FR in dezelfde knoprij (geen extra venster).
const isMandate = (name) => /(mandat|mandaat|assigner)/i.test(name || "");
const isFilled = (name) => / - ingevuld\.pdf$/i.test(name || "");
// Taal van het mandaat: "mandaat" is Nederlands, "mandat"/"assigner" Frans.
const mandateLang = (name) => (/mandaat/i.test(name || "") ? "nl" : "fr");
// Werkbon standaard in het Frans voor Wallonie (postcodes 1300-1499 en 4000-7999), anders Nederlands.
const defaultLang = (postalCode) => {
    const pc = parseInt(postalCode, 10);
    return (pc >= 1300 && pc <= 1499) || (pc >= 4000 && pc <= 7999) ? "fr" : "nl";
};
const errorText = (e) =>
    (e && e.response && e.response.data && (e.response.data.error || e.response.data.detail)) || (e && e.message) || "";

const SignedCopyButton = ({ projectId, file, onChanged }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);

    const onPick = async (event) => {
        const chosen = event.target.files && event.target.files[0];
        event.target.value = "";
        if (!chosen) return;
        setBusy(true);
        setError(false);
        try {
            await uploadSignedCopy(projectId, file.name, chosen);
            if (onChanged) await onChanged();
        } catch (e) {
            console.error("Error uploading signed copy:", e);
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <span className="signed-copy">
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: "none" }}
                onChange={onPick}
            />
            <button
                type="button"
                className="signed-copy-button"
                disabled={busy}
                onClick={() => inputRef.current && inputRef.current.click()}
            >
                {busy ? <FaSpinner className="loading-icon" /> : <Trans>Add signed copy</Trans>}
            </button>
            {error && (
                <span className="signed-copy-error">
                    <Trans>Upload failed, please try again.</Trans>
                </span>
            )}
        </span>
    );
};

// Thumbnail van één foto uit voorafDocs; laadt zelf en geeft de blob-url weer vrij.
const Thumbnail = ({ projectId, file, onOpen }) => {
    const [src, setSrc] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let url = null;
        let cancelled = false;
        getProjectThumbUrl(projectId, file.path)
            .then((u) => {
                url = u;
                if (cancelled) URL.revokeObjectURL(u);
                else setSrc(u);
            })
            .catch((e) => {
                console.error("Error loading thumbnail:", e);
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [projectId, file.path]);

    return (
        <button type="button" className="extra-thumb" onClick={() => onOpen(file)} title={file.name}>
            {src ? (
                <img src={src} alt={file.name} />
            ) : failed ? (
                <span className="extra-thumb-name">{file.name}</span>
            ) : (
                <FaSpinner className="loading-icon" />
            )}
        </button>
    );
};

const ExtraProjectFiles = ({ extraFiles, projectId, postalCode, onChanged }) => {
    const loadError = !!(extraFiles && !Array.isArray(extraFiles) && extraFiles.error);
    const noFiles = extraFiles === null;
    const files = useMemo(() => (Array.isArray(extraFiles) ? extraFiles : []), [extraFiles]);
    const documents = useMemo(() => files.filter((file) => !isImage(file.name)), [files]);
    const images = useMemo(() => files.filter((file) => isImage(file.name)), [files]);
    const [signing, setSigning] = useState(null);
    const [opening, setOpening] = useState(null);     // pad van het document dat opengaat
    const [openError, setOpenError] = useState(null); // pad van het document dat niet opende
    const [viewer, setViewer] = useState(null);       // { name, url, error }
    const [lang, setLang] = useState(() => defaultLang(postalCode));
    const [preparing, setPreparing] = useState(null); // "werkbon" of het pad van het mandaat
    const [prepareError, setPrepareError] = useState(null); // { key, message }
    const [warning, setWarning] = useState(null);     // foto's die niet in de pdf konden

    // Werkbon of ingevuld mandaat laten maken en meteen het tekenvenster openen.
    const prepareAndSign = async (key, options) => {
        setPrepareError(null);
        setWarning(null);
        setPreparing(key);
        try {
            const result = await createWerkbon(projectId, options);
            const skipped = (result.warnings || []).flatMap((w) => w.skipped_photos || []);
            if (skipped.length) setWarning(skipped.join(", "));
            setSigning({ name: result.name, path: result.path });
            if (onChanged) onChanged();
        } catch (e) {
            console.error("Error creating werkbon:", e);
            setPrepareError({ key, message: errorText(e) });
        } finally {
            setPreparing(null);
        }
    };

    const startSigning = (file) => {
        if (isMandate(file.name) && !isFilled(file.name)) {
            prepareAndSign(file.path, { lang: mandateLang(file.name), source: file.name });
        } else {
            setSigning(file);
        }
    };

    // Document openen: het tabblad gaat meteen open (anders blokkeert de gsm het als pop-up), de
    // tijdelijke Dropbox-link komt er daarna in.
    const openDocument = async (file) => {
        setOpenError(null);
        setOpening(file.path);
        const win = window.open("", "_blank");
        try {
            const { url } = await getProjectFileLink(projectId, file.path);
            if (win) {
                win.opener = null;
                win.location.href = url;
            } else {
                window.location.assign(url);
            }
        } catch (e) {
            console.error("Error opening file:", e);
            if (win) win.close();
            setOpenError(file.path);
        } finally {
            setOpening(null);
        }
    };

    const openImage = async (file) => {
        setViewer({ name: file.name, url: null, error: false });
        try {
            const { url } = await getProjectFileLink(projectId, file.path);
            setViewer((v) => (v && v.name === file.name ? { ...v, url } : v));
        } catch (e) {
            console.error("Error opening image:", e);
            setViewer((v) => (v && v.name === file.name ? { ...v, error: true } : v));
        }
    };

    return (
        <div>
            {signing && (
                <SignPdfDialog
                    file={signing}
                    projectId={projectId}
                    onClose={() => setSigning(null)}
                    onDone={onChanged}
                />
            )}
            {viewer && (
                <div className="image-viewer-overlay" role="dialog" aria-modal="true" onClick={() => setViewer(null)}>
                    <button type="button" className="image-viewer-close" aria-label="close" onClick={() => setViewer(null)}>
                        <FaTimes />
                    </button>
                    {viewer.error ? (
                        <p className="signed-copy-error image-viewer-message"><Trans>Could not load this document.</Trans></p>
                    ) : viewer.url ? (
                        <img src={viewer.url} alt={viewer.name} className="image-viewer-img" />
                    ) : (
                        <FaSpinner className="loading-icon image-viewer-message" />
                    )}
                </div>
            )}
            <h1><Trans>Extra Project Files</Trans>{!loadError && !noFiles && files.length === 0 && <FaSpinner className="loading-icon" />}</h1>
            {projectId && (
                <div className="werkbon-row">
                    <button
                        type="button"
                        className={`werkbon-lang${lang === "nl" ? " werkbon-lang-active" : ""}`}
                        onClick={() => setLang("nl")}
                        disabled={!!preparing}
                    >
                        NL
                    </button>
                    <button
                        type="button"
                        className={`werkbon-lang${lang === "fr" ? " werkbon-lang-active" : ""}`}
                        onClick={() => setLang("fr")}
                        disabled={!!preparing}
                    >
                        FR
                    </button>
                    <button
                        type="button"
                        className="sign-button"
                        disabled={!!preparing}
                        onClick={() => prepareAndSign("werkbon", { lang })}
                    >
                        {preparing === "werkbon" ? <FaSpinner className="loading-icon" /> : <Trans>Create work order and sign</Trans>}
                    </button>
                    {preparing === "werkbon" && <span><Trans>Filling in hours, materials and photos...</Trans></span>}
                    {prepareError && prepareError.key === "werkbon" && (
                        <span className="signed-copy-error">
                            <Trans>The work order could not be created:</Trans> {prepareError.message}
                        </span>
                    )}
                </div>
            )}
            {warning && (
                <p className="signed-copy-error"><Trans>These photos could not be added to the document:</Trans> {warning}</p>
            )}
            {noFiles && <p><Trans>No files in voorafDocs.</Trans></p>}
            {loadError && (
                <p className="signed-copy-error"><Trans>Could not load the project files. Reload the page to try again.</Trans></p>
            )}
            {files.length > 0 ? (
                <div className="extra-files-section">
                    <h2><Trans>Files:</Trans></h2>
                    <div className="extra-documents">
                        {documents.map((file) => (
                            <div key={file.path || file.name} className="document-row">
                                <a
                                    href={`#${encodeURIComponent(file.name)}`}
                                    onClick={(e) => { e.preventDefault(); openDocument(file); }}
                                    className="document-link"
                                >
                                    {file.name}
                                </a>
                                {opening === file.path && <FaSpinner className="loading-icon" />}
                                {openError === file.path && (
                                    <span className="signed-copy-error"><Trans>Could not open this file, please try again.</Trans></span>
                                )}
                                {isPdf(file.name) && !isSigned(file.name) && projectId && (
                                    <>
                                        <button
                                            type="button"
                                            className="sign-button"
                                            disabled={!!preparing}
                                            onClick={() => startSigning(file)}
                                        >
                                            {preparing === file.path ? <FaSpinner className="loading-icon" /> : <Trans>Sign</Trans>}
                                        </button>
                                        <SignedCopyButton projectId={projectId} file={file} onChanged={onChanged} />
                                        {preparing === file.path && <span><Trans>Filling in hours, materials and photos...</Trans></span>}
                                        {prepareError && prepareError.key === file.path && (
                                            <span className="signed-copy-error">
                                                <Trans>The mandate could not be filled in:</Trans> {prepareError.message}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                    <h2><Trans>Images:</Trans></h2>
                    <div className="extra-images">
                        {images.map((file) => (
                            <Thumbnail key={file.path || file.name} projectId={projectId} file={file} onOpen={openImage} />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ExtraProjectFiles;
